import { adNoticeService } from '@/service/Index';

const XLOG_CACHE_KEY = 'xlog_switch_status_booking_pc';
const XLOG_CACHE_EXPIRE = 10 * 60 * 1000; // 10 分钟

let isXLogInited = false; // 是否已经初始化过

// 保存监听器引用
let onLoaded: ((event: any) => void) | null = null;

interface XLogCache {
    value: boolean;
    expire: number;
}

// ========== 兼容性检测 ==========
function isIE11() {
    return !!window.MSInputMethodContext && !!document.documentMode;
}

function isOldChrome(version: number = 96) {
    const userAgent: string = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome) {
        const match = userAgent.match(/Chrome\/(\d+)/);
        if (match) {
            const chromeVersion = parseInt(match[1], 10);
            return chromeVersion < version;
        }
    }
    return false;
}

// ========== 缓存工具 ==========
function getCache(): XLogCache | null {
    const cacheStr = localStorage.getItem(XLOG_CACHE_KEY);
    if (!cacheStr) return null;
    try {
        return JSON.parse(cacheStr) as XLogCache;
    } catch {
        console.log('[XLog] 缓存解析失败');
        return null;
    }
}

function setCache(value: boolean) {
    localStorage.setItem(
        XLOG_CACHE_KEY,
        JSON.stringify({ value, expire: Date.now() + XLOG_CACHE_EXPIRE })
    );
    console.log(`[XLog] 写入缓存: ${value}, 有效期 ${XLOG_CACHE_EXPIRE / 1000} 秒`);
}

// ========== 获取开关状态 ==========
async function getXLogStatus(): Promise<boolean> {
    const cache = getCache();
    if (cache && Date.now() < cache.expire) {
        const remain = Math.round((cache.expire - Date.now()) / 1000);
        console.log(`[XLog] 使用缓存状态: ${cache.value}, 剩余 ${remain} 秒`);
        return cache.value;
    }

    console.log('[XLog] 缓存不存在或已过期 → 调用接口');
    try {
        const res: any = await adNoticeService.getXLogStatus({});
        console.log('[XLog] 接口返回:', res);

        if (res?.code === 0) {
            const enabled = !(res.data === 'false');
            setCache(enabled);
            return enabled;
        } else {
            console.log('[XLog] 接口返回异常，默认开启');
            setCache(true);
            return true;
        }
    } catch (err) {
        console.log('[XLog] 获取接口状态失败，默认开启', err);
        setCache(true);
        return true;
    }
}

// ========== 注册逻辑 ==========
export async function initXLog() {
    const enabled = await getXLogStatus();
    if (enabled) {
        await enableXLog();
    } else {
        disableXLog();
        console.log('[XLog] 当前关闭状态，未注册');
    }
}

export async function enableXLog() {
    if (isIE11() || isOldChrome(96)) {
        console.log('[XLog] 浏览器版本过低，跳过注册');
        window.XLog = null;
        return;
    }

    const enabled = await getXLogStatus();
    if (!enabled) {
        console.log('[XLog] 开关关闭 → 跳过注册');
        return;
    }
    if (isXLogInited) {
        console.log('[XLog] 已初始化过 → 跳过注册');
        return;
    }

    try {
        const { default: XLog } = await import('@szhotel/front-metric-h5');
        const [
            { default: errorPlugin },
            { default: performancePlugin },
            { default: recordPlugin },
        ] = await Promise.all([
            import('@szhotel/front-metric-h5-plugin-error'),
            import('@szhotel/front-metric-h5-plugin-performance'),
            import('@szhotel/front-metric-h5-plugin-record'),
        ]);

        window.XLog = XLog({
            type: 'sz_dkb',    // 组名
            project: 'e_booking_pc',  // 项目/平台
            plateform: 'e_booking_pc',
            sendUrl: 'https://tlog.elong.com/log/batch',
        });

        // 监听器
        onLoaded = (event: any) => console.log('[XLog] 初始化完成 >>>', event);

        window.XLog.use(errorPlugin, { traceUrlList: ['/ebooking.elong.com/*'] });
        window.XLog.use(performancePlugin, { samplingRate: 0.5 });
        window.XLog.use(recordPlugin, {
            startRecord: true,
            sampling: {
                mousemove: true,
                mouseInteraction: true,
                scroll: 1000,
                input: 'last',
            },
        });

        window.addEventListener('_xlogLoaded', onLoaded);

        isXLogInited = true;
        console.log('[XLog] 初始化成功 + 插件已挂载');
    } catch (err) {
        console.log('[XLog] XLog 初始化失败:', err);
    }
}

export function disableXLog() {
    if (window.XLog) {
        if (onLoaded) window.removeEventListener('_xlogLoaded', onLoaded);

        window.XLog = null;
        console.log('[XLog] 已关闭并彻底注销');
    }
    isXLogInited = false;
    onLoaded = null;
}

// ========== 安全调用 ==========
export async function safeXLogSend(event: string, data: any) {
    const cache = getCache();
    const now = Date.now();
    const cacheValid = cache && now < cache.expire;

    if (cacheValid) {
        if (!cache.value) {
            console.log('[XLog] 缓存状态关闭 → 跳过 send');
            disableXLog();
            return;
        }
    } else {
        const enabled = await getXLogStatus();
        if (!enabled) {
            console.log('[XLog] 接口状态关闭 → 跳过 send');
            disableXLog();
            return;
        }
    }

    if (!window.XLog) {
        await enableXLog();
    }

    if (window.XLog) {
        console.log(`[XLog] 发送事件: ${event}`, data);
        window.XLog.send(event, data);
    } else {
        console.log('[XLog] send 被跳过（初始化失败）');
    }
}